/**
 * Zone slice — zone list (entity adapter, newest first) plus `item`, the
 * currently opened zone with populated locations/subZones as returned by
 * GET /zones/:id. The map view (ZonesDetails) works entirely off `item`.
 *
 * `status` doubles as a signal channel: components watch for values like
 * "create_succeeded" to trigger navigation, so the exact strings matter.
 */
import { createSlice, createEntityAdapter } from "@reduxjs/toolkit";

import {
  doGetAllZones,
  doCreateZone,
  doUpdateZone,
  doDetailZone,
  doDeleteZone,
  doReGeocodeZone,
} from "./thunk";

const zonesAdapter = createEntityAdapter({
  selectId: (item) => item._id,
  sortComparer: (a, b) => b.createdAt.localeCompare(a.createdAt),
});

const zoneSlice = createSlice({
  name: "zone",
  initialState: {
    isFetching: false,
    status: "idle",
    metadata: {},
    entities: [],
    item: null,
    ids: [],
  },
  reducers: {
    // Patches one location inside the opened zone (state.item) after a
    // location edit, so the map updates without refetching the whole zone.
    updateLocationInsideZone(state, action) {
      const { zone, locationUpdated } = action.payload;
      const locations = [...zone.locations] || [];
      const locationIndexUpdate = locations.findIndex(l => l._id === locationUpdated._id)
      if (locationIndexUpdate === -1) return;
      locations[locationIndexUpdate] = locationUpdated
      state.item = { ...zone, locations }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(doGetAllZones.pending, (state) => {
      state.isFetching = true;
      state.action = "list";
      state.status = "loading";
    });
    builder.addCase(doGetAllZones.fulfilled, (state, action) => {
      const { zones, meta } = action.payload;
      state.isFetching = false;
      state.status = "succeeded";
      state.metadata = meta;
      zonesAdapter.setAll(state, zones);
    });
    builder.addCase(doGetAllZones.rejected, (state) => {
      state.isFetching = false;
      state.status = "failed";
    });
    builder.addCase(doCreateZone.pending, (state) => {
      state.isFetching = true;
      state.status = "loading";
    });
    builder.addCase(doCreateZone.fulfilled, (state, action) => {
      state.isFetching = false;
      state.status = "create_succeeded";
      state.item = action.payload;
      zonesAdapter.upsertOne(state, action.payload);
    });
    builder.addCase(doCreateZone.rejected, (state) => {
      state.isFetching = false;
      state.status = "failed";
    });
    builder.addCase(doUpdateZone.pending, (state) => {
      state.isFetching = true;
      state.status = "loading";
    });
    builder.addCase(doUpdateZone.fulfilled, (state, action) => {
      state.isFetching = false;
      state.status = "succeeded";
      zonesAdapter.upsertOne(state, action.payload);
    });
    builder.addCase(doUpdateZone.rejected, (state) => {
      state.isFetching = false;
      state.status = "failed";
    });
    builder.addCase(doDetailZone.pending, (state) => {
      state.isFetching = true;
      state.status = "loading";
    });
    builder.addCase(doDetailZone.fulfilled, (state, action) => {
      state.isFetching = false;
      state.status = "succeeded";
      state.item = action.payload;
    });
    builder.addCase(doDetailZone.rejected, (state) => {
      state.isFetching = false;
      state.status = "failed";
    });
    builder.addCase(doDeleteZone.pending, (state) => {
      state.isFetching = true;
      state.status = "loading";
    });
    builder.addCase(doDeleteZone.fulfilled, (state, action) => {
      state.isFetching = false;
      state.status = "succeeded";
      state.message = action.payload.message;
      zonesAdapter.removeOne(state, action.meta.arg);
    });
    builder.addCase(doDeleteZone.rejected, (state) => {
      state.isFetching = false;
      state.status = "failed";
    });
    builder.addCase(doReGeocodeZone.pending, (state) => {
      state.isFetching = true;
      state.status = "loading";
    });
    builder.addCase(doReGeocodeZone.fulfilled, (state, action) => {
      state.isFetching = false;
      state.status = "succeeded";
      state.message = action.payload.message;
    });
    builder.addCase(doReGeocodeZone.rejected, (state) => {
      state.isFetching = false;
      state.status = "failed";
    });
  },
});

export const {
  selectAll: selectAllZones,
  selectById: selectZoneById,
  selectIds: selectZoneIds,
} = zonesAdapter.getSelectors((state) => state.zone);

const { actions, reducer: zoneReducer } = zoneSlice;

export const { updateLocationInsideZone } = actions;
export default zoneReducer;
